import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Events from '@EventEmitter';
import appConstants from '@appConstants';
import { postData } from '@componentAPIs';
import { redirectToIBPlanning } from '../../../ShipmentSelection/utils';
import { shouldNavigateWithCityRoutes, isPathInIBPlanning } from './summaryUtils';

const prefetchSetUserPreferences = (scenarioId, terminalId, onSuccess) => {
  const promise = postData('SetUserPreferences', { activeScenarioId: scenarioId, terminalId })
    .then((response) => {
      const isSuccess = response && Math.trunc(response.status / 100) === 2;
      if (isSuccess) onSuccess();
      return { response, isSuccess };
    })
    .catch(() => ({ response: null, isSuccess: false }));
  return promise;
};

const useCreateRoutes = ({ etlSummary, userData, isEnforcedDynamicRoutingEnabled, onCloseSummary, enqueueSnackbar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Prefetch status: 'idle' | 'pending' | 'success' | 'error'
  const prefetchRef = useRef({ status: 'idle', promise: null });

  useEffect(() => {
    if (!etlSummary?.scenarioId || !userData?.userProfile?.terminal?.id) return;
    // Fire SetUserPreferences + getUserInfo in the background as soon as the summary is shown,
    // so the button click can skip both API round-trips if they already completed.
    setIsPrefetching(true);
    const promise = prefetchSetUserPreferences(
      etlSummary.scenarioId,
      userData.userProfile.terminal.id,
      () => Events.emit('RefreshUserInfo')
    ).then(({ isSuccess }) => {
      prefetchRef.current.status = isSuccess ? 'success' : 'error';
      setIsPrefetching(false);
      return { status: isSuccess ? 200 : 500 };
    });
    prefetchRef.current = { status: 'pending', promise };
  }, []);

  const doNavigate = (cityRoutes, shouldUseCityRoutes, scenarioId) => {
    onCloseSummary?.();
    if (shouldUseCityRoutes && cityRoutes?.length > 0 && scenarioId) {
      const targetPath = appConstants.NAV_URLS.IBPLANNING_ROUTES;
      if (location.pathname === targetPath) {
        // Already on routes tab — emit event so header opens DR dialog without a path change
        Events.emit('OpenDynamicRoutingWithCityRoutes');
      } else if (isPathInIBPlanning(location.pathname)) {
        // Same /ibplanning parent — use RedirectToPage so header handles the child tab switch
        Events.emit('RedirectToPage', null, targetPath);
      } else {
        // Outside IB Planning (e.g. Shipment Selection) — navigate directly
        navigate(targetPath, { replace: true, state: { cityRoutes } });
      }
    } else {
      redirectToIBPlanning(navigate)();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (pendingNavigation && userData?.userProfile?.scenarioID === pendingNavigation.scenarioId) {
      doNavigate(pendingNavigation.cityRoutes, pendingNavigation.shouldUseCityRoutes, pendingNavigation.scenarioId);
      setPendingNavigation(null);
    }
  }, [userData?.userProfile?.scenarioID, pendingNavigation]);

  const onSetUserPreferencesSuccess = () => {
    enqueueSnackbar(`Active scenario: ${etlSummary?.scenarioName} `, { variant: 'success' });
    const targetScenarioId = etlSummary?.scenarioId;
    const shouldUseCityRoutes = shouldNavigateWithCityRoutes(isEnforcedDynamicRoutingEnabled, etlSummary?.cityRoutes);
    if (targetScenarioId === userData?.userProfile?.scenarioID) {
      doNavigate(etlSummary?.cityRoutes, shouldUseCityRoutes, targetScenarioId);
    } else {
      setPendingNavigation({ scenarioId: targetScenarioId, cityRoutes: etlSummary?.cityRoutes, shouldUseCityRoutes });
      Events.emit('RefreshUserInfo');
    }
  };

  const onSetUserPreferencesFailure = () => {
    enqueueSnackbar(`Data refresh failed for ${userData.userProfile.terminal.code}`, { variant: 'error' });
    setIsLoading(false);
    doNavigate(
      etlSummary?.cityRoutes,
      shouldNavigateWithCityRoutes(isEnforcedDynamicRoutingEnabled, etlSummary?.cityRoutes),
      etlSummary?.scenarioId
    );
  };

  const handleCreateRoutes = () => {
    setIsLoading(true);
    const { status, promise } = prefetchRef.current;
    const onError = () => {
      setIsLoading(false);
      enqueueSnackbar('Failed to create routes', { variant: 'error' });
    };

    if (status === 'success') {
      onSetUserPreferencesSuccess();
    } else if (status === 'pending') {
      promise.then(({ status: httpStatus }) => {
        if (httpStatus === 200) onSetUserPreferencesSuccess();
        else onSetUserPreferencesFailure();
      }).catch(onError);
    } else {
      postData('SetUserPreferences', {
        activeScenarioId: etlSummary?.scenarioId,
        terminalId: userData.userProfile.terminal.id
      }).then((response) => {
        if (response && Math.trunc(response.status / 100) === 2) onSetUserPreferencesSuccess();
        else onSetUserPreferencesFailure();
      }).catch(onError);
    }
  };

  return { isLoading, isPrefetching, handleCreateRoutes };
};

export default useCreateRoutes;
